import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/api/client';
import {
  LayoutDashboard, Image, PlusCircle, MessageSquare, User,
  BarChart2, ClipboardList, Users, Tag, Layers, Settings, FileText, Bell,
  Menu, LogOut, ShieldCheck, ChevronRight, Receipt, CreditCard, Search, Globe
} from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

const MEMBER_NAV = [
  { path: '/dashboard', label: 'Overview', icon: LayoutDashboard, exact: true },
  { path: '/dashboard/listings', label: 'My Listings', icon: Image },
  { path: '/dashboard/listings/create', label: 'Create Listing', icon: PlusCircle },
  { path: '/dashboard/enquiries', label: 'Enquiries', icon: MessageSquare },
  { path: '/dashboard/transactions', label: 'Transactions', icon: Receipt },
  { path: '/dashboard/profile', label: 'Account Settings', icon: User },
];

const ADMIN_NAV = [
  { path: '/admin', label: 'Analytics', icon: BarChart2, exact: true, group: 'Overview' },
  { path: '/admin/applications', label: 'Applications', icon: ClipboardList, group: 'People' },
  { path: '/admin/members', label: 'Members', icon: Users, group: 'People' },
  { path: '/admin/listings', label: 'All Listings', icon: Image, group: 'Content' },
  { path: '/admin/conversations', label: 'Conversations', icon: MessageSquare, group: 'Content' },
  { path: '/admin/transactions', label: 'Transactions', icon: CreditCard, group: 'Content' },
  { path: '/admin/site-content', label: 'Site Content', icon: FileText, group: 'Content' },
  { path: '/admin/notifications', label: 'Notifications', icon: Bell, group: 'Content' },
  { path: '/admin/categories', label: 'Categories', icon: Tag, group: 'Settings' },
  { path: '/admin/listing-types', label: 'Listing Types', icon: Layers, group: 'Settings' },
  { path: '/admin/blocked-words', label: 'Blocked Words', icon: ShieldCheck, group: 'Settings' },
  { path: '/admin/config', label: 'Config', icon: Settings, group: 'Settings' },
];

function AdminSidebarContent({ user, logout, location, navigate }) {
  const groups = ['Overview', 'People', 'Content', 'Settings'];
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    api.get('/admin/stats/pending-listings')
      .then(r => setPendingCount(r.data.count || 0))
      .catch(() => {});
  }, [location.pathname]);

  return (
    <div className="flex flex-col h-full text-sm font-sans" style={{ backgroundColor: '#111827', color: '#9ca3af', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Brand */}
      <div className="px-6 py-6 border-b border-gray-800">
        <Link to="/" className="block">
          <div className="flex flex-col">
            <img src="/assets/images/logo.png" alt="Messums Art Market" className="h-20 w-auto object-contain mb-1" style={{ objectPosition: 'left' }} />
            <p className="text-[10px] text-blue-400 uppercase tracking-wide font-semibold mt-1">Pro Edition</p>
          </div>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 overflow-y-auto" data-testid="admin-sidebar">
        {groups.map(group => {
          const items = ADMIN_NAV.filter(i => i.group === group);
          return (
            <div key={group} className="mb-6">
              <p className="px-3 mb-2 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                {group}
              </p>
              {items.map(item => {
                const isActive = item.exact
                  ? location.pathname === item.path
                  : location.pathname.startsWith(item.path);
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`sidebar-item-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                    className={`flex items-center gap-3 px-3 py-2.5 mb-1 rounded-xl transition-all duration-200 ${isActive ? 'bg-gray-800 text-white shadow-sm' : 'hover:bg-gray-800 hover:text-white'}`}
                    style={{ fontWeight: isActive ? 600 : 500 }}
                  >
                    <Icon size={16} className={isActive ? "text-blue-400" : "text-gray-400"} style={{ flexShrink: 0 }} />
                    <span className="flex-1">{item.label}</span>
                    {item.label === 'All Listings' && pendingCount > 0 && (
                      <span
                        className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-500 text-white min-w-[20px] text-center"
                        data-testid="pending-review-badge"
                      >
                        {pendingCount > 99 ? '99+' : pendingCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>
    </div>
  );
}

function MemberSidebarContent({ user, logout, location, navigate }) {
  return (
    <div className="flex flex-col h-full text-sm font-sans" style={{ backgroundColor: '#111827', color: '#9ca3af', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Brand */}
      <div className="px-6 py-6 border-b border-gray-800">
        <Link to="/" className="block">
          <div className="flex flex-col">
            <img src="/assets/images/logo.png" alt="Messums Art Market" className="h-20 w-auto object-contain mb-1" style={{ objectPosition: 'left' }} />
            <p className="text-[10px] text-blue-400 uppercase tracking-wide font-semibold mt-1">Member Portal</p>
          </div>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 overflow-y-auto" data-testid="member-sidebar">
        <div className="space-y-1">
          {MEMBER_NAV.map((item) => {
            const isActive = item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path);
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 transition-colors ${
                  isActive ? 'bg-gray-800 text-white font-medium' : 'hover:bg-gray-800 hover:text-gray-300'
                }`}
                data-testid={`sidebar-item-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <Icon size={18} className={isActive ? 'text-blue-400' : 'text-gray-400'} />
                {item.label}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

export default function DashboardShell({ children, title }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isAdmin = location.pathname.startsWith('/admin');
  const SidebarComp = isAdmin ? AdminSidebarContent : MemberSidebarContent;

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#f8fafc', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex flex-col flex-shrink-0 shadow-lg"
        style={{ width: '260px', zIndex: 10 }}
      >
        <SidebarComp user={user} logout={logout} location={location} navigate={navigate} />
      </aside>

      {/* Mobile drawer ... */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="relative w-64 bg-gray-900 flex-shrink-0 shadow-xl">
            <SidebarComp user={user} logout={logout} location={location} navigate={navigate} />
          </div>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header
          className="flex items-center justify-between px-8 flex-shrink-0 bg-white border-b border-gray-200"
          style={{ height: '72px' }}
        >
          {/* Mobile menu */}
          <div className="flex items-center gap-4 lg:hidden">
            <button onClick={() => setMobileOpen(true)} className="p-2 -ml-2 rounded-lg hover:bg-gray-200">
              <Menu size={20} />
            </button>
            <h1 className="text-lg font-bold text-gray-800">{title}</h1>
          </div>

          <div className="hidden lg:flex items-center flex-1">
            <div className="relative w-96">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" placeholder="Search websites..." className="w-full bg-white border border-gray-200 rounded-full py-2.5 pl-11 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm" />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors relative">
              <Bell size={20} />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border-2 border-white"></span>
            </button>
            <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
              <Settings size={20} />
            </button>
            
            <div className="relative group flex items-center ml-2 border-l pl-4 border-gray-200 cursor-pointer">
              <div className="w-9 h-9 rounded-full flex items-center justify-center bg-blue-500 text-white font-bold text-sm">
                {user?.name?.[0]?.toUpperCase() || 'U'}
              </div>
              {/* Dropdown Menu (Hover) */}
              <div className="absolute right-0 top-full mt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                <div className="bg-white border border-gray-100 shadow-lg rounded-xl overflow-hidden min-w-[160px]">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-800 truncate">{user?.name}</p>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide truncate">{user?.role}</p>
                  </div>
                  <button
                    onClick={() => { logout(); navigate(isAdmin ? '/admin/login' : '/login'); }}
                    className="flex items-center gap-2 px-4 py-3 text-sm text-red-600 hover:bg-red-50 w-full text-left transition-colors font-medium"
                    data-testid="header-logout"
                  >
                    <LogOut size={16} />
                    Sign out
                  </button>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
